import { FlexibleModuleEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '@modules/page-builder/types';

/**
 * The boxes the revamp crops the article's pictures into, as one definition.
 *
 * Shared by the reader's page and the Business Manager page builder. The
 * builder needs them twice over: to draw the picture in the box it will land
 * in, and to hand the image editor the same box as its crop frame. Without
 * that second use an author crops to the upload's own ratio and the reader's
 * page silently takes more off — the seeded `ColumnDimension` recipes are all
 * 3:2 or 2:3, and none of the boxes below is.
 *
 * Taken from the design rather than from the upload (Figma "Page Builder /
 * Image"): two cells whose photographs happen to differ by a few pixels of
 * ratio would render at different heights, and the row would have no baseline.
 */

/** A portrait large fills its 900px cell. */
export const REVAMP_PORTRAIT_LARGE = '900 / 1190';
/** A portrait small stands 438x615 in the cell. */
export const REVAMP_PORTRAIT_SMALL = '438 / 615';
/** A landscape large runs 1520x800 on the page's 200px margins. */
export const REVAMP_LANDSCAPE_LARGE = '1520 / 800';
/** A landscape in a cell: the wide crop taken to the cell's width. */
export const REVAMP_CELL_LANDSCAPE = '900 / 474';

/**
 * Landscape images go full width with side insets; portraits stay a column.
 * Read off the module's first column, so a pair is judged as a pair.
 */
export const isLandscapeModule = (module: PageBuilderModuleType): boolean => {
    const { width, height } = module.columns[0]?.dimension ?? {};
    return Boolean(width && height && width >= height);
};

/**
 * The box this module's pictures are drawn into, or `undefined` where the
 * upload's own ratio stands.
 *
 * Undefined is the right answer for the galleries: their tiles are square by
 * the `Gallery square` dimension the preset seeds, and the reader's page lets
 * that dimension through rather than pinning a box over it.
 */
export const revampImageAspectRatio = (
    module: Pick<PageBuilderModuleType, 'presetType' | 'columns'>,
): string | undefined => {
    const landscape = isLandscapeModule(module as PageBuilderModuleType);
    switch (module.presetType) {
        case FlexibleModuleEnum.SingleImageModule:
            return landscape ? REVAMP_LANDSCAPE_LARGE : REVAMP_PORTRAIT_LARGE;
        case FlexibleModuleEnum.DuoImageModule:
            return landscape ? REVAMP_CELL_LANDSCAPE : REVAMP_PORTRAIT_LARGE;
        case FlexibleModuleEnum.ImageParagraphModule:
            return REVAMP_PORTRAIT_SMALL;
        default:
            return undefined;
    }
};

/**
 * The same box as a number, for the image editor's crop frame — which takes a
 * width/height ratio rather than a CSS `aspect-ratio` string.
 */
export const aspectRatioToNumber = (ratio: string): number | undefined => {
    const [width, height] = ratio.split('/').map((part) => Number(part.trim()));
    if (!width || !height) {
        return undefined;
    }
    return width / height;
};

/** The subset of a ColumnDimension the page builder's slot is drawn from. */
interface SlotDimension {
    width: number;
    height: number;
    mobileWidth: number;
    mobileHeight: number;
}

/**
 * The stored dimension with only its heights moved, so the builder's slot
 * stands in the box the reader's page draws.
 *
 * Widths and every other field are left alone on purpose. The dimension keeps
 * travelling with the column, where its `id` is written back as
 * `UpdateDimensionID`, its width and height are the minimum upload size, and
 * the upscale target reads them — none of which should follow the display box.
 */
export const fitDimensionToBox = <T extends SlotDimension>(
    dimension: T,
    boxRatio: number | undefined,
): T => {
    if (!boxRatio || !dimension.width) {
        return dimension;
    }
    return {
        ...dimension,
        height: Math.round(dimension.width / boxRatio),
        mobileHeight: dimension.mobileWidth
            ? Math.round(dimension.mobileWidth / boxRatio)
            : dimension.mobileHeight,
    };
};
